import { isEqual, omit } from 'es-toolkit'
import { GalleryVerticalEnd, Search } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { toast } from 'sonner'

import { addEpisodeFavorite, deleteProduct, getProductById, getProductProgress, removeEpisodeFavorite } from '@/api/api'
import type { Episode, EpisodeList, Product } from '@/api/type'
import { Header, type ToolbarItem } from '@/components/shared/header'
import { SwipeIndicator } from '@/components/shared/swipe-indicator'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { ActionSection, RadioSection } from '@/components/ui/custom-dropdown'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { PaginationContainer } from '@/components/ui/pagination-container'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useGoBack } from '@/hooks/useGoBack'
import { confirmDialog } from '@/lib/confirm-dialog'
import { FOOTER_HEIGHT } from '@/lib/constants'
import { PATHS } from '@/routes/paths'
import { useAppStore } from '@/store/useAppStore'
import { useMediaStore } from '@/store/useMediaStore'
import { useProgressStore } from '@/store/useProgressStore'
import { useUISettingsStore } from '@/store/useUISettingsStore'
import { NON_READABLE_MIME_TYPES } from '@/types/ebook-conversion'

import { MUSIC_DOMAIN } from '../music/constants'
import { ComicReader } from './comic-reader'
import { ComicItem } from './components/comic-item'
import { EpisodeDrawer } from './components/episode-drawer'
import { GridLayout } from './components/grid-layout'
import { InfiniteGridLayout } from './components/infinite-grid-layout'
import { JumpModal } from './components/jump-modal'
import { NotebooklmProgressBanner } from './components/notebooklm-progress-banner'
import { SearchPanel } from './components/search-panel'
import { useInfiniteProducts } from './hooks/useInfiniteProducts'
import { useMediaFetch } from './hooks/useMediaFetch'
import { useSwipeNavigation } from './hooks/useSwipeNavigation'

const VideoDomainList = ['zozo', 'monsnode', 'miss']

/** 商品一覧の取得条件に関わるクエリパラメータ（useMediaFetch の queryBuilder と対応） */
const SEARCH_QUERY_PARAM_KEYS = [
  'tags',
  'page',
  'min',
  'max',
  'title',
  'order_by',
  'order',
  'favorite',
  'reading_status',
] as const

/** オーバーレイ（右スワイプで戻る）対応のMIMEタイプ判定 */
const OVERLAY_MIME_TYPES = new Set(['application/zip', 'application/octet-stream', 'application/vnd.comicbook+zip'])
const isOverlayCompatible = (mimeType: string | null): boolean => {
  if (!mimeType) return true // mime_type 不明はオーバーレイ（従来挙動維持）
  return OVERLAY_MIME_TYPES.has(mimeType) || mimeType.startsWith('image/') || mimeType.startsWith('video/')
}

/** 一覧のデータからリーダーの初期表示用エピソードを組み立てる（mime_type が無いとリーダーを選べない） */
const toInitialEpisode = (product: Product, ep: EpisodeList | undefined): Episode | null => {
  if (!ep?.mime_type) return null
  return {
    product: omit(product, ['episodes']),
    episode_id: ep.episode_id,
    domain_episode_id: ep.domain_episode_id,
    created_at: ep.created_at,
    index: ep.index,
    num_pages: ep.num_pages,
    upload_date: ep.upload_date,
    description: ep.description,
    mime_type: ep.mime_type,
    custom_name: ep.custom_name,
    file_size: ep.file_size,
    file_name: '',
    is_favorite: ep.is_favorite,
  }
}

const SORT_OPTIONS = [
  { key: 'upload_date', label: 'アップロード日' },
  { key: 'created_at', label: '登録順' },
  { key: 'access_granted_at', label: '新着' },
  { key: 'last_read', label: '読んだ順' },
  { key: 'title', label: 'タイトル' },
  { key: 'num_pages', label: 'ページ数' },
  { key: 'random', label: 'ランダム' },
]

export const ProductContainer = () => {
  // クエリパラメータからdomainを取得（新しいパス構造: /p?domain=xxx）
  const [params, setParams] = useSearchParams()
  const domain = params.get('domain') ?? undefined

  // ユーザーのアクセス可能なドメイン一覧を取得
  const accessibleDomains = useAppStore((state) => state.accessibleDomains)
  const hasFullAccess = useAppStore((state) => state.hasFullAccess)

  const products = useMediaStore((state) => state.products)
  const getProducts = useMediaStore((state) => state.getProducts)
  const updateEpisodeFavorite = useMediaStore((state) => state.updateEpisodeFavorite)
  const updateStoreProductTitle = useMediaStore((state) => state.updateProductTitle)

  // アスペクト比設定とスクロール位置管理
  const {
    productAspectRatio,
    setProductAspectRatio,
    getProductScrollPosition,
    setProductScrollPosition,
    clearProductScrollPosition,
    isInfiniteScroll,
    setIsInfiniteScroll,
  } = useUISettingsStore()

  const loading = useAppStore((state) => state.loading)

  const navigate = useNavigate()
  const goBack = useGoBack()
  const { queryBuilder } = useMediaFetch()
  const [openDialog, setOpenDialog] = useState(false)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const scrollTopRef = useRef(0)
  const [columns, setColumns] = useState(3)
  const [hasRestoredOnMount, setHasRestoredOnMount] = useState(false)
  const previousPageRef = useRef<number>(1)

  // Episode Drawer state
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [lastEpisodeId, setLastEpisodeId] = useState<number | null>(null)

  // URLパラメータからエピソードシートを開く指示を取得
  const openEpisodesParam = params.get('openEpisodes')

  // 動画モード（ビデオドメインでも画像表示に切り替え可能）
  const [videoMode, setVideoMode] = useState(false)

  // 一括削除用のstate
  const [isDeleteMode, setIsDeleteMode] = useState(false)
  const [selectedForDeletion, setSelectedForDeletion] = useState<Set<number>>(new Set())

  // 削除結果ダイアログ用のstate
  const [deleteResultDialogOpen, setDeleteResultDialogOpen] = useState(false)
  const [deleteResult, setDeleteResult] = useState<{
    items: { title: string; episodeCount: number; files: string[] }[]
    totalEpisodes: number
    totalFiles: number
  } | null>(null)

  // 検索パネル用のstate
  const [isSearchOpen, setIsSearchOpen] = useState(false)

  // オーバーレイリーダー用のstate（URLパラメータ ep から初期化 → リロードで復元可能）
  const epParam = params.get('ep')
  const [overlayEpisodeId, _setOverlayEpisodeId] = useState<string | null>(epParam)
  const [overlayInitialEpisode, setOverlayInitialEpisode] = useState<Episode | null>(null)

  // overlayEpisodeIdの変更時にURLも同期する
  const setOverlayEpisodeId = useCallback(
    (id: string | null) => {
      _setOverlayEpisodeId(id)
      setParams(
        (prev) => {
          if (id) {
            prev.set('ep', id)
          } else {
            prev.delete('ep')
          }
          return prev
        },
        { replace: true },
      )
    },
    [setParams],
  )

  // URLのepパラメータが外部から変わった場合に同期
  useEffect(() => {
    _setOverlayEpisodeId(epParam)
  }, [epParam])

  const latestQuery = useRef(queryBuilder())

  // productsが更新されたらselectedProductも同期（削除・変換後のリフレッシュ対応）
  const selectedProductIdRef = useRef(selectedProduct?.product_id)
  useEffect(() => {
    selectedProductIdRef.current = selectedProduct?.product_id
  }, [selectedProduct?.product_id])

  // 現在のソートモード（URLパラメータから）
  const orderByParam = params.get('order_by')
  const orderParam = params.get('order')
  const currentSort = orderByParam ?? 'created_at'
  const defaultOrder = (sortKey: string) => (sortKey === 'title' ? 'asc' : 'desc')
  const isRandom = currentSort === 'random'
  const currentOrder = orderParam ?? defaultOrder(currentSort)

  // hasFullAccess の場合は「新着」(access_granted_at) を非表示
  const sortOptions = useMemo(
    () => (hasFullAccess ? SORT_OPTIONS.filter((o) => o.key !== 'access_granted_at') : SORT_OPTIONS),
    [hasFullAccess],
  )
  const currentSortLabel = sortOptions.find((o) => o.key === currentSort)?.label ?? '登録順'
  const orderArrow = currentOrder === 'desc' ? '↓' : '↑'

  // ソートBottomSheet用のローカルstate
  const [isSortSheetOpen, setIsSortSheetOpen] = useState(false)
  const [pendingSort, setPendingSort] = useState(currentSort)
  const [pendingOrder, setPendingOrder] = useState(currentOrder)

  // シートを開くときに現在の値で初期化
  const openSortSheet = useCallback(() => {
    setPendingSort(currentSort)
    setPendingOrder(currentOrder)
    setIsSortSheetOpen(true)
  }, [currentSort, currentOrder])

  // サブミット: URLパラメータを更新してAPI実行
  const handleSortSubmit = useCallback(() => {
    setParams((prev) => {
      if (pendingSort === 'created_at') {
        prev.delete('order_by')
      } else {
        prev.set('order_by', pendingSort)
      }
      if (pendingOrder === 'desc') {
        prev.delete('order')
      } else {
        prev.set('order', pendingOrder)
      }
      prev.set('page', '1')
      return prev
    })
    setIsSortSheetOpen(false)
  }, [setParams, pendingSort, pendingOrder])

  // 変更があるかどうか
  const hasSortChanges = pendingSort !== currentSort || pendingOrder !== currentOrder

  // Toolbar: ソートボタン（BottomSheetトリガー）
  const sortToolbar = useMemo(
    () => (
      <Button variant="ghost" size="sm" className="gap-1 text-white/80 text-xs h-7 px-2" onClick={openSortSheet}>
        <span>
          {currentSortLabel}
          {isRandom ? '' : ` ${orderArrow}`}
        </span>
      </Button>
    ),
    [currentSortLabel, orderArrow, openSortSheet, isRandom],
  )

  // Toolbar: 検索アイコン
  const toolbarItems: ToolbarItem[] = useMemo(
    () => [
      {
        icon: <GalleryVerticalEnd size={18} className={isInfiniteScroll ? 'text-blue-400' : 'text-white/80'} />,
        label: '無限スクロール',
        onClick: () => {
          if (isInfiniteScroll) {
            // 無限→ページネーション: 現在表示中のページ番号をURLに反映
            const activePage = infiniteActivePageRef.current
            setParams((prev) => {
              prev.set('page', activePage.toString())
              return prev
            })
          }
          setIsInfiniteScroll(!isInfiniteScroll)
        },
      },
      {
        icon: <Search size={18} className="text-white/80" />,
        label: '検索',
        onClick: () => setIsSearchOpen(true),
      },
    ],
    [isInfiniteScroll, setIsInfiniteScroll, setParams],
  )

  // ContainerDropdownのsectionsを定義
  const dropdownSections: (RadioSection | ActionSection)[] = useMemo(
    () => [
      {
        type: 'radio' as const,
        label: 'Column',
        value: `col-${columns}`,
        onValueChange: (value) => setColumns(Number(String(value).split('-')[1])),
        items: [
          { key: 'col-1', label: '1', value: 'col-1' },
          { key: 'col-2', label: '2', value: 'col-2' },
          { key: 'col-3', label: '3', value: 'col-3' },
          { key: 'col-4', label: '4', value: 'col-4' },
          { key: 'col-5', label: '5', value: 'col-5' },
        ],
      },
      {
        type: 'radio' as const,
        label: 'Aspect Ratio',
        value: `aspect-${productAspectRatio}`,
        onValueChange: (value) => setProductAspectRatio(String(value).split('-')[1] as 'landscape' | 'portrait'),
        items: [
          { key: 'aspect-portrait', label: '縦長', value: 'aspect-portrait' },
          { key: 'aspect-landscape', label: '横長', value: 'aspect-landscape' },
        ],
      },
      {
        type: 'action' as const,
        label: 'Actions',
        items: [
          {
            key: 'jump',
            label: 'Open Jump Dialog',
            onClick: () => setOpenDialog(true),
          },
          ...(hasFullAccess
            ? [
                {
                  key: 'bulk-delete',
                  label: isDeleteMode ? '削除モード終了' : '一括削除',
                  onClick: () => {
                    setIsDeleteMode(!isDeleteMode)
                    setSelectedForDeletion(new Set())
                  },
                },
              ]
            : []),
          ...(VideoDomainList.includes(domain ?? '')
            ? [
                {
                  key: 'video-mode',
                  label: videoMode ? '動画モード OFF' : '動画モード ON',
                  onClick: () => setVideoMode((prev) => !prev),
                },
              ]
            : []),
          // 欠番チェックは line ドメイン専用
          ...(hasFullAccess && domain === 'line'
            ? [
                {
                  key: 'contents-check',
                  label: '欠番チェック',
                  onClick: () => navigate(PATHS.CHECK),
                },
              ]
            : []),
        ],
      },
    ],
    [columns, productAspectRatio, setProductAspectRatio, hasFullAccess, isDeleteMode, domain, videoMode, navigate],
  )

  const page = useMemo(() => {
    const pageParams = params.get('page')
    if (pageParams) return Number(pageParams)
    return 1
  }, [params])

  const currentHeight = domain && page ? getProductScrollPosition(domain, page) : 0

  // paramsから検索条件を直接取得（useCallbackの依存を減らすため）
  // オーバーレイリーダーが書き込む表示設定クエリ（zoom / direction など）では
  // 再フェッチが走らないよう、検索条件に関わるパラメータだけを対象にする
  const paramsString = useMemo(
    () => SEARCH_QUERY_PARAM_KEYS.map((key) => `${key}=${params.get(key) ?? ''}`).join('&'),
    [params],
  )

  // 無限スクロール用のベースクエリ（page以外のフィルタ/ソート条件）
  const infiniteBaseQuery = useMemo(() => {
    const query = queryBuilder()
    // pageは各ページフェッチ時に設定するので除外
    delete query.page
    query.per_page = 60
    query.with_episodes = true
    return query
  }, [queryBuilder])

  const {
    allProducts: infiniteProducts,
    minPage: infiniteMinPage,
    maxPage: infiniteMaxPage,
    totalPages: infiniteTotalPages,
    fetchPage,
    isLoadingPrev,
    isLoadingNext,
    reset: resetInfinite,
    refetchAll: refetchInfinite,
    updateEpisodeProgress: updateInfiniteEpisodeProgress,
    updateProductTitle: updateInfiniteProductTitle,
  } = useInfiniteProducts({ baseQuery: infiniteBaseQuery, domain, perPage: 60 })

  // productsが更新されたらselectedProductも同期（削除・変換後のリフレッシュ対応）
  useEffect(() => {
    if (!selectedProductIdRef.current) return
    const source = isInfiniteScroll ? infiniteProducts : products?.products
    if (!source) return
    const updated = source.find((p) => p.product_id === selectedProductIdRef.current)
    if (updated) {
      setSelectedProduct(updated)
    }
  }, [products, infiniteProducts, isInfiniteScroll])

  // 無限スクロールモード初期化（モードON時に現在ページをロード）
  const infiniteInitRef = useRef(false)
  useEffect(() => {
    if (!isInfiniteScroll) {
      infiniteInitRef.current = false
      return
    }
    if (infiniteInitRef.current) return
    infiniteInitRef.current = true
    resetInfinite()
    fetchPage(page)
  }, [isInfiniteScroll, page, fetchPage, resetInfinite])

  // フィルタ/ソート/ドメイン変更時に無限スクロールキャッシュをリセット
  const prevInfiniteQueryRef = useRef(infiniteBaseQuery)
  const prevDomainRef = useRef(domain)
  useEffect(() => {
    if (!isInfiniteScroll) return
    const queryChanged = !isEqual(infiniteBaseQuery, prevInfiniteQueryRef.current)
    const domainChanged = domain !== prevDomainRef.current
    if (!queryChanged && !domainChanged) return
    prevInfiniteQueryRef.current = infiniteBaseQuery
    prevDomainRef.current = domain
    resetInfinite()
    infiniteInitRef.current = false
    fetchPage(page)
  }, [isInfiniteScroll, infiniteBaseQuery, domain, page, fetchPage, resetInfinite])

  // 無限スクロール: 前後ページロードコールバック
  const handleLoadPrev = useCallback(() => {
    if (infiniteMinPage > 1) fetchPage(infiniteMinPage - 1)
  }, [infiniteMinPage, fetchPage])

  const handleLoadNext = useCallback(() => {
    if (infiniteMaxPage < infiniteTotalPages) fetchPage(infiniteMaxPage + 1)
  }, [infiniteMaxPage, infiniteTotalPages, fetchPage])

  // 進捗保存イベントを無限スクロールのローカルデータに同期
  const lastSavedProgress = useProgressStore((state) => state.lastSavedProgress)
  useEffect(() => {
    if (!lastSavedProgress || !isInfiniteScroll) return
    const { episodeId, progress, isCompleted } = lastSavedProgress
    updateInfiniteEpisodeProgress(episodeId, progress, isCompleted)
  }, [lastSavedProgress, isInfiniteScroll, updateInfiniteEpisodeProgress])

  // 無限スクロール: アクティブページ変更時にURL同期（モード切替時に必要）
  const infiniteActivePageRef = useRef(page)
  const handleActivePageChange = useCallback((pageNum: number) => {
    infiniteActivePageRef.current = pageNum
  }, [])

  useEffect(() => {
    const query = queryBuilder()
    query.per_page = 60
    query.with_episodes = true

    // domainを含めた完全なクエリで比較
    // ランダムの場合は毎回新しい結果を取得するため比較をスキップ
    const fullQuery = { domain, ...query }
    if (query.order_by !== 'random' && isEqual(fullQuery, latestQuery.current)) {
      return
    }
    latestQuery.current = fullQuery
    getProducts(fullQuery)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, paramsString, hasFullAccess])

  // URLパラメータ openEpisodes に基づいて自動的にエピソードシートを開く
  useEffect(() => {
    if (!openEpisodesParam) return
    if (!products?.products) return

    const productIdToOpen = parseInt(openEpisodesParam, 10)
    if (isNaN(productIdToOpen)) return

    const openDrawer = (product: Product) => {
      setSelectedProduct(product)
      setLastEpisodeId(null)
      setIsDrawerOpen(true)
      getProductProgress(product.product_id).then((progress) => {
        setLastEpisodeId(progress?.last_episode_id ?? null)
      })
      setParams((prev) => {
        prev.delete('openEpisodes')
        return prev
      })
    }

    // まずロード済みproductsから検索
    const targetProduct = products.products.find((p) => p.product_id === productIdToOpen)
    if (targetProduct && targetProduct.episodes && targetProduct.episodes.length > 0) {
      openDrawer(targetProduct)
      return
    }

    // ロード済みに無い場合はAPIから取得
    getProductById(productIdToOpen).then((product) => {
      if (product && product.episodes && product.episodes.length > 0) {
        openDrawer(product)
      } else {
        setParams((prev) => {
          prev.delete('openEpisodes')
          return prev
        })
      }
    })
  }, [openEpisodesParam, products?.products, setParams])

  const handlePageChange = useCallback(
    (page: number) => {
      // ページ変更時はスクロール位置をリセット
      if (scrollRef.current) scrollRef.current.scrollTop = 0
      scrollTopRef.current = 0
      if (domain) clearProductScrollPosition(domain, page)

      setParams((prev) => {
        const pageParams = prev.get('page')
        if (!pageParams) {
          prev.set('page', page.toString())
          return prev
        }
        const pageParamsNumber = Number(pageParams)

        if (pageParamsNumber < 1 || pageParamsNumber > (products?.last_page ?? 1)) {
          return prev
        }
        prev.set('page', page.toString())
        return prev
      })
    },
    [products?.last_page, setParams, domain, clearProductScrollPosition],
  )

  // スワイプナビゲーションのカスタムフック（オーバーレイ中 or 無限スクロール中はブロック）
  const { swipeHandler, swipeProgress, swipeDirection } = useSwipeNavigation({
    onPageChange: handlePageChange,
    currentPage: page,
    lastPage: products?.last_page,
    disabled: !!overlayEpisodeId || isInfiniteScroll,
  })

  const handleProductClick = useCallback(
    (productId: number) => {
      // 現在のスクロール位置を保存
      if (domain && page) {
        setProductScrollPosition(domain, page, scrollTopRef.current)
      }

      // 対象のプロダクトを取得（無限スクロール時はinfiniteProductsからも検索）
      const targetProduct =
        (isInfiniteScroll
          ? infiniteProducts.find((p) => p.product_id === productId)
          : products?.products.find((p) => p.product_id === productId)) ?? null

      if (!targetProduct) return

      // 「すべて」表示では URL の domain が無いため、作品自身の domain で分岐する
      const productDomain = domain ?? targetProduct.domain

      // audiobook ドメインの場合は audio/mpeg エピソードに直接遷移
      if (productDomain === 'audiobook') {
        const audioEpisode = targetProduct.episodes?.find((ep) => ep.mime_type === 'audio/mpeg')
        if (audioEpisode) {
          navigate(`/p/${audioEpisode.episode_id}`)
        }
        return
      }

      // エピソードが2件以上、またはsingleでもブラウザで読めないフォーマットの場合はドロワーを開く
      const firstEpisode = targetProduct.episodes?.[0]
      const shouldOpenDrawer =
        (targetProduct.episodes && targetProduct.episodes.length >= 2) ||
        (firstEpisode?.mime_type && NON_READABLE_MIME_TYPES.has(firstEpisode.mime_type))

      if (shouldOpenDrawer) {
        setSelectedProduct(targetProduct)
        setLastEpisodeId(null) // 一旦nullにリセット
        setIsDrawerOpen(true) // 先にドロワーを開く
        // バックグラウンドで進捗を取得
        getProductProgress(targetProduct.product_id).then((progress) => {
          setLastEpisodeId(progress?.last_episode_id ?? null)
        })
        return
      }

      // 直接リーダーを開く
      if (firstEpisode) {
        if (isOverlayCompatible(firstEpisode.mime_type)) {
          setOverlayInitialEpisode(toInitialEpisode(targetProduct, firstEpisode))
          setOverlayEpisodeId(firstEpisode.episode_id.toString())
        } else {
          navigate(`/p/${firstEpisode.episode_id}`)
        }
      }
    },
    [
      domain,
      navigate,
      products?.products,
      infiniteProducts,
      isInfiniteScroll,
      page,
      setProductScrollPosition,
      setOverlayEpisodeId,
    ],
  )

  // エピソード選択時のハンドラー（episode_id を使用）
  const handleEpisodeSelect = useCallback(
    (episodeId: number) => {
      if (!selectedProduct) return
      const episode = selectedProduct.episodes?.find((ep) => ep.episode_id === episodeId)
      const mimeType = episode?.mime_type
      setIsDrawerOpen(false)
      if (mimeType && !isOverlayCompatible(mimeType)) {
        navigate(`/p/${episodeId}`)
      } else {
        setOverlayInitialEpisode(toInitialEpisode(selectedProduct, episode))
        setOverlayEpisodeId(episodeId.toString())
      }
    },
    [selectedProduct, setOverlayEpisodeId, navigate],
  )

  // お気に入りトグルハンドラー
  const handleFavoriteToggle = useCallback(
    async (episodeId: number, currentFavorite: boolean) => {
      try {
        if (currentFavorite) {
          await removeEpisodeFavorite(episodeId)
          toast.success('お気に入りから削除しました')
        } else {
          await addEpisodeFavorite(episodeId)
          toast.success('お気に入りに追加しました')
        }
        // productsを再取得して更新
        getProducts(latestQuery.current)
        if (isInfiniteScroll) refetchInfinite()
      } catch (error) {
        console.error('Error toggling favorite:', error)
        toast.error('お気に入りの更新に失敗しました')
      }
    },
    [getProducts, isInfiniteScroll, refetchInfinite],
  )

  // タイトル変更を該当プロダクトだけに反映する（再取得するとスクロール位置が失われるため）
  const handleTitleUpdated = useCallback(
    (productId: number, title: string) => {
      updateStoreProductTitle(productId, title)
      updateInfiniteProductTitle(productId, title)
    },
    [updateStoreProductTitle, updateInfiniteProductTitle],
  )

  // 削除選択トグルハンドラー
  const handleToggleDeleteSelection = useCallback((productId: number) => {
    setSelectedForDeletion((prev) => {
      const next = new Set(prev)
      if (next.has(productId)) {
        next.delete(productId)
      } else {
        next.add(productId)
      }
      return next
    })
  }, [])

  // 削除処理の共通ロジック（一括削除・単体削除で共用）
  const executeDelete = useCallback(
    async (productIds: number[]) => {
      if (productIds.length === 0) return
      try {
        const deletedItems: { title: string; episodeCount: number; files: string[] }[] = []

        for (const productId of productIds) {
          const result = await deleteProduct(productId)
          deletedItems.push({
            title: result.deleted.product.title,
            episodeCount: result.deleted.episodes.length,
            files: result.deleted_files,
          })
        }

        const totalEpisodes = deletedItems.reduce((sum, item) => sum + item.episodeCount, 0)
        const totalFiles = deletedItems.reduce((sum, item) => sum + item.files.length, 0)

        setDeleteResult({
          items: deletedItems,
          totalEpisodes,
          totalFiles,
        })
        setDeleteResultDialogOpen(true)

        getProducts(latestQuery.current)
        if (isInfiniteScroll) refetchInfinite()
      } catch (error) {
        console.error('Error deleting products:', error)
        toast.error('削除に失敗しました')
      }
    },
    [getProducts, isInfiniteScroll, refetchInfinite],
  )

  // 単体削除ハンドラー（ドロップダウンメニューから）
  const handleSingleDelete = useCallback(
    async (productId: number, title: string) => {
      if (!(await confirmDialog({ title: `「${title}」を削除しますか？`, destructive: true }))) return
      await executeDelete([productId])
    },
    [executeDelete],
  )

  // 一括削除ハンドラー
  const handleBulkDelete = useCallback(async () => {
    if (selectedForDeletion.size === 0) return
    if (!(await confirmDialog({ title: `${selectedForDeletion.size}件を削除しますか？`, destructive: true }))) return
    await executeDelete(Array.from(selectedForDeletion))
    setSelectedForDeletion(new Set())
    setIsDeleteMode(false)
  }, [selectedForDeletion, executeDelete])

  // 削除モード終了
  const handleCancelDeleteMode = useCallback(() => {
    setIsDeleteMode(false)
    setSelectedForDeletion(new Set())
  }, [])

  // スクロール位置復元（GridLayoutのinitialScrollTopでも復元するが、データ遅延時のフォールバック）
  useEffect(() => {
    if (
      products &&
      products.products.length > 0 &&
      scrollRef.current &&
      currentHeight &&
      currentHeight > 0 &&
      !hasRestoredOnMount
    ) {
      setHasRestoredOnMount(true)
      requestAnimationFrame(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = currentHeight
      })
    }
  }, [products, currentHeight, hasRestoredOnMount])

  // ページまたはドメインが変更された時にフラグをリセット
  useEffect(() => {
    const pageChanged = previousPageRef.current !== page

    setHasRestoredOnMount(false)

    // ページが実際に変更された場合のみスクロール位置をリセット
    if (pageChanged && domain) {
      clearProductScrollPosition(domain, page)
      previousPageRef.current = page
    }
  }, [page, domain, clearProductScrollPosition])

  const isVideoDomain = useMemo(() => {
    return VideoDomainList.includes(domain ?? '')
  }, [domain])

  // --- GridLayout 用の安定コールバック ---
  const handleScrollRef = useCallback((el: HTMLDivElement | null) => {
    scrollRef.current = el
  }, [])

  const handleGridScroll = useCallback((scrollTop: number) => {
    scrollTopRef.current = scrollTop
  }, [])

  const renderItem = useCallback(
    (index: number) => {
      const target = products?.products[index]
      if (!target) return null
      return (
        <ComicItem
          target={target}
          domain={domain ?? target.domain}
          index={index}
          isVideoDomain={isVideoDomain && videoMode}
          onItemClick={handleProductClick}
          onFavoriteToggle={handleFavoriteToggle}
          isDeleteMode={isDeleteMode}
          isSelectedForDeletion={selectedForDeletion.has(target.product_id)}
          onToggleDeleteSelection={handleToggleDeleteSelection}
          onDeleteProduct={handleSingleDelete}
          onTitleUpdated={handleTitleUpdated}
          aspectRatio={productAspectRatio}
        />
      )
    },
    [
      products?.products,
      domain,
      isVideoDomain,
      videoMode,
      handleProductClick,
      handleFavoriteToggle,
      isDeleteMode,
      selectedForDeletion,
      handleToggleDeleteSelection,
      handleSingleDelete,
      handleTitleUpdated,
      productAspectRatio,
    ],
  )

  // 無限スクロール用のrenderItem（isImageActive付き）
  const renderInfiniteItem = useCallback(
    (globalIndex: number, isImageActive: boolean) => {
      const target = infiniteProducts[globalIndex]
      if (!target) return null
      return (
        <ComicItem
          target={target}
          domain={domain ?? target.domain}
          index={globalIndex}
          isVideoDomain={isVideoDomain && videoMode}
          onItemClick={handleProductClick}
          onFavoriteToggle={handleFavoriteToggle}
          isDeleteMode={isDeleteMode}
          isSelectedForDeletion={selectedForDeletion.has(target.product_id)}
          onToggleDeleteSelection={handleToggleDeleteSelection}
          onDeleteProduct={handleSingleDelete}
          onTitleUpdated={handleTitleUpdated}
          aspectRatio={productAspectRatio}
          hideImage={!isImageActive}
        />
      )
    },
    [
      infiniteProducts,
      domain,
      isVideoDomain,
      videoMode,
      handleProductClick,
      handleFavoriteToggle,
      isDeleteMode,
      selectedForDeletion,
      handleToggleDeleteSelection,
      handleSingleDelete,
      handleTitleUpdated,
      productAspectRatio,
    ],
  )

  // ドメイン変更ハンドラー
  const handleDomainChange = useCallback(
    (newDomain: string) => {
      setParams((prev) => {
        if (newDomain === 'all') {
          prev.delete('domain') // ALLの場合はdomainパラメータを削除
        } else {
          prev.set('domain', newDomain)
        }
        // タグなどの検索条件はドメインごとに意味が変わるためリセット（ソート設定は維持）
        for (const key of SEARCH_QUERY_PARAM_KEYS) {
          if (key === 'order_by' || key === 'order') continue
          prev.delete(key)
        }
        return prev
      })
    },
    [setParams],
  )

  // loading中、またはproductsがない場合は何も表示しない
  if (loading > 0 || !products) {
    return null
  }

  return (
    <div {...swipeHandler} className="h-full relative">
      {/* スワイプインジケーター */}
      <SwipeIndicator swipeDirection={swipeDirection} swipeProgress={swipeProgress} currentPage={page} />

      <div className="bg-black text-white text-center h-full w-full absolute top-0 left-0 right-0 flex flex-col">
        <Header
          title={
            <Select value={domain || 'all'} onValueChange={handleDomainChange}>
              <SelectTrigger className="w-35 font-bold text-lg border-none">
                <SelectValue>{domain ? domain.toUpperCase() : 'ALL'}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ALL</SelectItem>
                {accessibleDomains
                  .filter((d) => d !== MUSIC_DOMAIN)
                  .map((d) => (
                    <SelectItem key={d} value={d}>
                      {d.toUpperCase()}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          }
          onClose={() => {
            if (domain && page) {
              setProductScrollPosition(domain, page, scrollTopRef.current)
            }
            goBack()
          }}
          dropdownSections={dropdownSections}
          customToolbar={sortToolbar}
          toolbarItems={toolbarItems}
        />

        <NotebooklmProgressBanner />

        <div style={{ paddingBottom: isInfiniteScroll ? 0 : FOOTER_HEIGHT }} className="flex-1 overflow-hidden">
          {isInfiniteScroll ? (
            infiniteProducts.length > 0 ? (
              <InfiniteGridLayout
                renderItem={renderInfiniteItem}
                initialPage={page}
                totalPages={infiniteTotalPages}
                perPage={60}
                totalItems={infiniteProducts.length}
                colSize={columns}
                aspectRatio={productAspectRatio}
                onLoadPrev={handleLoadPrev}
                onLoadNext={handleLoadNext}
                isLoadingPrev={isLoadingPrev}
                isLoadingNext={isLoadingNext}
                minPage={infiniteMinPage}
                maxPage={infiniteMaxPage}
                onActivePageChange={handleActivePageChange}
              />
            ) : (
              <div className="w-screen h-screen flex justify-center items-center text-4xl">No products found.</div>
            )
          ) : products.products.length ? (
            <div key={`grid-${page}`} className="h-full">
              <GridLayout
                scrollRef={handleScrollRef}
                initialScrollTop={currentHeight}
                onScroll={handleGridScroll}
                colSize={columns}
                length={products?.products.length}
                componentTemplate={renderItem}
                aspectRatio={productAspectRatio}
              />
            </div>
          ) : (
            <div className="w-screen h-screen flex justify-center items-center text-4xl">No products found.</div>
          )}
        </div>

        {/* 下部固定ページネーション */}
        {/* 削除モード時のアクションバー */}
        {isDeleteMode && (
          <div className="fixed bottom-12 left-0 right-0 bg-red-600/95 backdrop-blur-sm py-3 px-4 z-50 flex items-center justify-between">
            <span className="text-white font-medium">{selectedForDeletion.size}件選択中</span>
            <div className="flex gap-2">
              <button
                onClick={handleCancelDeleteMode}
                className="px-4 py-1.5 text-sm bg-white/20 hover:bg-white/30 text-white rounded-md transition-colors"
              >
                キャンセル
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={selectedForDeletion.size === 0}
                className="px-4 py-1.5 text-sm bg-white hover:bg-white/90 text-red-600 font-bold rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                削除
              </button>
            </div>
          </div>
        )}

        {/* 下部固定ページネーション */}
        {!isInfiniteScroll && (
          <div
            className="fixed bottom-0 left-0 right-0 bg-black/90 backdrop-blur-sm py-1 z-50 flex items-center justify-center"
            style={{ height: FOOTER_HEIGHT }}
          >
            <PaginationContainer
              currentPage={page}
              totalPages={products?.last_page ?? 1}
              onPageChange={handlePageChange}
              showPreviousNext
            />
          </div>
        )}
      </div>

      <JumpModal
        onJump={(page) => {
          setParams((prev) => {
            prev.set('page', `${page}`)
            return prev
          })
          setOpenDialog(false)
        }}
        currentIndex={page}
        maxIndex={products?.last_page ?? 1}
        open={openDialog}
        onClose={() => setOpenDialog(false)}
      />

      {/* Episode Selection Drawer */}
      {selectedProduct && (
        <EpisodeDrawer
          open={isDrawerOpen}
          onOpenChange={setIsDrawerOpen}
          episodes={selectedProduct.episodes}
          lastEpisodeId={lastEpisodeId}
          productTitle={selectedProduct.title}
          onEpisodeSelect={handleEpisodeSelect}
          onFavoriteUpdate={updateEpisodeFavorite}
          domain={domain ?? selectedProduct.domain}
          productId={selectedProduct.product_id}
          onRefresh={() => {
            getProducts(latestQuery.current)
            if (isInfiniteScroll) refetchInfinite()
          }}
        />
      )}

      {/* 削除結果ダイアログ */}
      <Dialog open={deleteResultDialogOpen} onOpenChange={setDeleteResultDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80dvh] flex flex-col">
          <DialogHeader>
            <DialogTitle>削除完了</DialogTitle>
            <DialogDescription>
              {deleteResult && (
                <>
                  {deleteResult.items.length}件のプロダクト（計{deleteResult.totalEpisodes}エピソード,{' '}
                  {deleteResult.totalFiles}ファイル）を削除しました
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {/* min-h-0 がないと自動サイズのトラックがコンテンツ分に膨らみ、max-h を超えてもスクロールしない */}
          <div className="flex-1 min-h-0 overflow-y-auto pr-4">
            {deleteResult?.items.map((item, idx) => (
              <div key={idx} className="mb-4">
                <h4 className="font-semibold text-sm mb-1">
                  {item.title}
                  <span className="text-muted-foreground font-normal ml-2">
                    ({item.episodeCount}話, {item.files.length}ファイル)
                  </span>
                </h4>
                {item.files.length > 0 && (
                  <ul className="text-xs text-muted-foreground space-y-0.5 ml-2">
                    {item.files.map((file, fileIdx) => (
                      <li key={fileIdx} className="font-mono break-all">
                        {file}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => setDeleteResultDialogOpen(false)}>閉じる</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ソート設定 BottomSheet */}
      <BottomSheet title="ソート" open={isSortSheetOpen} onOpenChange={setIsSortSheetOpen}>
        <div className="px-4 pb-6 space-y-4">
          <div className="space-y-1">
            <div className="text-sm text-muted-foreground mb-2">並び順</div>
            {sortOptions.map((opt) => (
              <button
                key={opt.key}
                onClick={() => {
                  setPendingSort(opt.key)
                  setPendingOrder(defaultOrder(opt.key))
                }}
                className={`w-full text-left px-3 py-2.5 rounded-md text-sm transition-colors ${
                  pendingSort === opt.key
                    ? 'bg-blue-500/20 text-blue-400 font-medium'
                    : 'text-white/80 hover:bg-white/10'
                }`}
              >
                {pendingSort === opt.key ? '✓ ' : '\u2003'}
                {opt.label}
              </button>
            ))}
          </div>
          <div className="space-y-1">
            <div className="text-sm text-muted-foreground mb-2">方向</div>
            <div className="flex gap-2">
              <button
                onClick={() => setPendingOrder('desc')}
                disabled={pendingSort === 'random'}
                className={`flex-1 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  pendingSort === 'random'
                    ? 'opacity-40 cursor-not-allowed text-white/40'
                    : pendingOrder === 'desc'
                      ? 'bg-blue-500/20 text-blue-400 font-medium'
                      : 'text-white/80 hover:bg-white/10'
                }`}
              >
                ↓ 降順
              </button>
              <button
                onClick={() => setPendingOrder('asc')}
                disabled={pendingSort === 'random'}
                className={`flex-1 px-3 py-2.5 rounded-md text-sm transition-colors ${
                  pendingSort === 'random'
                    ? 'opacity-40 cursor-not-allowed text-white/40'
                    : pendingOrder === 'asc'
                      ? 'bg-blue-500/20 text-blue-400 font-medium'
                      : 'text-white/80 hover:bg-white/10'
                }`}
              >
                ↑ 昇順
              </button>
            </div>
          </div>
          <Button className="w-full" onClick={handleSortSubmit} disabled={!hasSortChanges}>
            適用
          </Button>
        </div>
      </BottomSheet>

      {/* 検索パネル */}
      <BottomSheet
        title="検索"
        open={isSearchOpen}
        onOpenChange={setIsSearchOpen}
        snapPoints={[0.7, 1]}
        defaultSnapPoint={0.7}
      >
        <SearchPanel key={domain ?? 'all'} domain={domain} onClose={() => setIsSearchOpen(false)} />
      </BottomSheet>

      {/* オーバーレイリーダー（ProductContainerの上にfixedで表示） */}
      {overlayEpisodeId && (
        <div className="fixed inset-0 z-50">
          <ComicReader
            overlayEpisodeId={overlayEpisodeId}
            initialEpisode={overlayInitialEpisode}
            onOverlayEpisodeChange={setOverlayEpisodeId}
            onOverlayClose={() => setOverlayEpisodeId(null)}
            onError={(message) => {
              setOverlayEpisodeId(null)
              toast.error(message)
            }}
          />
        </div>
      )}
    </div>
  )
}
